import type { components } from './openapi-schema';
import { extractErrorMessage, readResponseBody, resolveUrl } from './http';
import { notifyUnauthorized } from './unauthorized-handler';

export type BusinessValidationError = components['schemas']['ValidationError'];

interface BusinessRequestErrorOptions {
  status?: number;
  validationError?: BusinessValidationError | null;
}

export class BusinessRequestError extends Error {
  status?: number;
  validationError: BusinessValidationError | null;

  constructor(message: string, options: BusinessRequestErrorOptions = {}) {
    super(message);
    this.name = 'BusinessRequestError';
    this.status = options.status;
    this.validationError = options.validationError ?? null;
  }
}

function isValidationError(payload: unknown): payload is BusinessValidationError {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const record = payload as Record<string, unknown>;
  return typeof record.field === 'string' && typeof record.errorType === 'string';
}

export function isBusinessRequestError(error: unknown): error is BusinessRequestError {
  return error instanceof BusinessRequestError;
}

export function replacePathParams(
  pathTemplate: string,
  params: Record<string, string | number>
) {
  return pathTemplate.replace(/\{([^}]+)\}/g, (_, key: string) =>
    encodeURIComponent(String(params[key]))
  );
}

export function buildQueryString(
  query: Record<string, string | number | boolean | null | undefined>
) {
  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    searchParams.set(key, String(value));
  });

  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : '';
}

function isFormDataBody(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

export async function performAuthorizedRequest<ResponseBody>(
  token: string,
  operation: {
    method: string;
    path: string;
  },
  options: {
    body?: unknown;
    networkErrorMessage: string;
    requestErrorMessage: string;
  }
) {
  let response: Response;
  const rawBody = options.body;
  const hasBody = rawBody !== undefined;
  const bodyIsFormData = isFormDataBody(rawBody);
  const requestBody: BodyInit | undefined = hasBody
    ? bodyIsFormData
      ? (rawBody as FormData)
      : JSON.stringify(rawBody)
    : undefined;

  try {
    response = await fetch(resolveUrl(operation.path), {
      method: operation.method.toUpperCase(),
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...(hasBody && !bodyIsFormData ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(requestBody !== undefined ? { body: requestBody } : {}),
    });
  } catch (error) {
    throw new BusinessRequestError(
      error instanceof Error
        ? options.networkErrorMessage
        : 'Não foi possível ligar ao servidor.'
    );
  }

  const payload = await readResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      notifyUnauthorized();
    }

    throw new BusinessRequestError(
      extractErrorMessage(payload, `${options.requestErrorMessage} (${response.status})`),
      {
        status: response.status,
        validationError: isValidationError(payload) ? payload : null,
      }
    );
  }

  return (payload ?? null) as ResponseBody;
}
